
import { useState, useEffect } from 'react';
import { MapPin, Car, User, CheckCircle, ArrowLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import RouteStep from './steps/RouteStep';
import VehicleStep from './steps/VehicleStep';
import DetailsStep from './steps/DetailsStep';
import SummaryStep from './steps/SummaryStep';
import MapComponent from '../Map';
import { Location, RouteInfo } from '../../types/maps';
import type { Database } from '../../integrations/supabase/types';

export type BookingData = {
  pickup: Location | null;
  destination: Location | null;
  date: string;
  time: string;
  passengers: number;
  luggage: number;
  flightNumber?: string;
  selectedVehicle: Database['public']['Tables']['vehicles']['Row'] | null;
  routeInfo: RouteInfo | null;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  isRoundTrip?: boolean;
};

const steps = [
  { id: 1, title: 'Trajet', icon: MapPin },
  { id: 2, title: 'Véhicule', icon: Car },
  { id: 3, title: 'Coordonnées', icon: User },
  { id: 4, title: 'Confirmation', icon: CheckCircle },
];

const BookingWizard = () => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState<BookingData>({
    pickup: null,
    destination: null,
    date: '',
    time: '',
    passengers: 1,
    luggage: 0,
    selectedVehicle: null,
    routeInfo: null,
  });

  // Scroll to top when component mounts or when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    if (location.state) {
      const { pickup, destination } = location.state;
      if (pickup || destination) {
        setBookingData(prev => ({
          ...prev,
          pickup: pickup || null,
          destination: destination || null
        }));
      }
    }
  }, [location.state]);

  const updateBookingData = (data: Partial<BookingData>) => {
    setBookingData((prev) => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, steps.length));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <RouteStep
            bookingData={bookingData}
            onUpdate={updateBookingData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <VehicleStep
            bookingData={bookingData}
            onUpdate={updateBookingData}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <DetailsStep
            bookingData={bookingData}
            onUpdate={updateBookingData}
            onNext={handleNext}
          />
        );
      case 4:
        return (
          <SummaryStep
            bookingData={bookingData}
            onUpdate={updateBookingData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          {currentStep > 1 && (
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </button>
          )}
          <h1 className="text-3xl font-serif font-bold text-center flex-grow text-secondary-dark">
            Book Your Premium Transfer
          </h1>
        </div>

        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      currentStep === step.id
                        ? 'bg-primary text-white'
                        : currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    <StepIcon className="w-5 h-5" />
                  </div>
                  <span
                    className={`ml-2 ${
                      currentStep === step.id ? 'text-primary font-medium' : 'text-gray-500'
                    }`}
                  >
                    {step.title}
                  </span>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-12 h-1 ml-4 ${
                        currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-lg border border-gray-100">
            {renderStep()}
          </div>
          <div className="lg:col-span-2 h-[600px] rounded-lg overflow-hidden shadow-lg">
            <MapComponent
              pickup={bookingData.pickup}
              destination={bookingData.destination}
              onRouteUpdate={(routeInfo: RouteInfo) => updateBookingData({ routeInfo })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingWizard;
